import { Box } from '@mui/material'
import i18n from 'i18next'
import PropTypes from 'prop-types'
import React, { memo, useEffect, useRef, useState } from 'react'
import {useSelector} from 'react-redux'
import { darkOverrides, lightOverrides } from './constants'
import Datafeed from './dataFeed'
import { useDefaultComponentTools } from '../../../../hooks/useDefaultComponentTools'
import { widget } from '../../../../libs/charting_library/charting_library.min'
import {selectTradeLines} from '../../../../store/redusers/chart/selectors'
import {selectOpenTradeById} from '../../../../store/redusers/positions/selectors'
import { selectRiskData } from '../../../../store/redusers/user/selectors'
import { getSimpleLang } from '../../../../helpers'
import { setSlLine, setStateStopLoss, setStateTakeProfit, setTpLine } from '../../../../store/redusers/orders/actions'


const ChartWidget = memo(({ assetTitle }) => {
	const [chartWidget, setChartWidget] = useState(null)
	const [orderLine, setOrderLine] = useState(null)
	const [positionLine, setPositionLine] = useState(null)
	const justFirstInit = useRef(false)
	const { theme, mdDown, dispatch } = useDefaultComponentTools()
	const isDark = theme.palette.mode === 'dark'
	const overrides = isDark ? darkOverrides : lightOverrides
	const tradeLines = useSelector(selectTradeLines)
	const myTrade = useSelector(selectOpenTradeById(tradeLines))
	const riskData = useSelector(selectRiskData)
	const orderLineColor = theme.palette.red
	const positionLineColor = theme.palette.blue

	useEffect(() => {
		removeLines()
		if(!tradeLines) return
		const myStopLoss =  myTrade.stopLoss
		const myTakeProfit =  myTrade.takeProfit
		if (chartWidget != null && myStopLoss !== 0 || myTakeProfit !== 10000000) {
			const currentInterval = chartWidget.symbolInterval(function () {
			})
			chartWidget.setSymbol(myTrade.assetName, currentInterval.interval)
			localStorage.setItem(
				'trading-selected-asset',
				JSON.stringify({
					'assetSoId':`${'asset_' + myTrade.assetId}`,
					'assetType': myTrade.assetClass
				})
			)
			const stopLossText = (myStopLoss !== 0) ? 'SL-'+myTrade.id : ''
			const takeProfitText = (myTakeProfit !== 10000000) ? 'TP-'+myTrade.id : ''
			addTradeLines(myStopLoss, myTakeProfit, stopLossText, takeProfitText)
		}
		function addTradeLines(stopLoss, takeProfit, stopLossText = null, takeProfitText = null){
			chartWidget.onChartReady(function() {
				if(stopLossText && stopLoss){
					const order = chartWidget.chart().createOrderLine()
					setOrderLine(order)
					order.setText(stopLossText)
						.setLineLength(3)
						.setLineStyle(0)
						.setQuantity(stopLoss)
						.setPrice(stopLoss)
						.setLineColor(orderLineColor)
						.setBodyBorderColor(orderLineColor)
						.setBodyTextColor(orderLineColor)
						.setQuantityBackgroundColor(orderLineColor)
						.setQuantityBorderColor(orderLineColor)
						.onMove(function() {
							this.setPrice(order.getPrice())
							this.setQuantity(order.getPrice())
							dispatch(setSlLine(order.getPrice()))
						})
				}
				if(takeProfitText && takeProfit){
					const position = chartWidget.chart().createOrderLine()
					setPositionLine(position)
					position.setText(takeProfitText)
						.setLineLength(3)
						.setLineStyle(0)
						.setQuantity(takeProfit)
						.setPrice(takeProfit)
						.setLineColor(positionLineColor)
						.setBodyBorderColor(positionLineColor)
						.setBodyTextColor(positionLineColor)
						.setQuantityBackgroundColor(positionLineColor)
						.setQuantityBorderColor(positionLineColor)
						.onMove(function() {
							this.setPrice(position.getPrice())
							this.setQuantity(position.getPrice())
							dispatch(setTpLine(position.getPrice()))
						})
				}
			})
		}
	}, [tradeLines])

	const removeLines = () => {
		if(orderLine) {
			orderLine.remove()
			setOrderLine(null)
		}
		if(positionLine) {
			positionLine.remove()
			setPositionLine(null)
		}
	}

	useEffect(() => {
		if(justFirstInit.current) return
		initChart()
	}, [])

	useEffect(() => {
		if(!justFirstInit.current) return
		initChart()
	}, [riskData])

	useEffect(() => {
		if(!chartWidget) return
		chartWidget?.changeTheme(theme.palette.mode)
		setTimeout(() => {
			chartWidget?.applyOverrides(overrides)
		}, 0)
	}, [isDark])

	useEffect(() => {
		if(!chartWidget) return
		removeLines()
		setTimeout(() => {
			chartWidget?.chart()?.setSymbol(assetTitle)
		}, 0)
	}, [assetTitle])

	const initChart = () => {
		justFirstInit.current = true
		const widgetOptions = {
			autosize: true,
			container_id: 'tv_box',
			custom_css_url: '../chart-style.css',
			datafeed: Datafeed,
			disabled_features: [
				'compare_symbol',
				'header_fullscreen_button',
				'header_compare',
				'header_symbol_search',
				'header_undo_redo',
				'symbol_search_hot_key',
				'create_volume_indicator_by_default',
				'widget_logo',
				'link_to_tradingview',
				'go_to_date'
			],
			fullscreen: false,
			has_no_volume: false,
			interval: '1',
			library_path: process.env.REACT_APP_URL_LIBS,
			locale: getSimpleLang(i18n.language) || 'en',
			overrides,
			preset: mdDown? 'mobile' : '',
			symbol: assetTitle,
			theme: theme.palette.mode,
			time_frames: [
				{ resolution: '1D', text: '3m' },
				{ resolution: '1D', text: '1m'  },
				{ resolution: '60', text: '7d' },
				{ resolution: '5', text: '3d' },
				{ resolution: '1', text: '1d' }
				// { resolution: '30', text: '1m' },
				// { resolution: '60', text: '3m' },
				// { resolution: '1', text: '1d' },
				// { resolution: '5', text: '5d'  }
			],
			user_id: 50
		}
		setChartWidget(new widget(widgetOptions))
	}

	return (
		<Box
			sx={{ height: '100%'}} id='tv_box'
		>
		</Box>
	)
})

ChartWidget.propTypes = {
	assetTitle: PropTypes.string
}

ChartWidget.displayName = 'ChartWidget'

export default ChartWidget
